import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class CommunicationTypeReadOnly extends ModelObject<CommunicationTypeReadOnly> {

    /**
    * Sequence for ordering communication types.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class CommunicationType extends ModelObjectWithDirtyCheck<CommunicationType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Communication Type Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;
    public readOnly: CommunicationTypeReadOnly = new CommunicationTypeReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
          this.readOnly = new CommunicationTypeReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "CommunicationType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class CommunicationTypeService extends ServiceBase {

  public async getAllCommunicationTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CommunicationType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.communicationTypes.map((v: any) => new CommunicationType(v));;
    return this.makeJsonRequest("communicationType/v1/getAllCommunicationTypes", req, false, extractor, opts);
  };

  public async getCommunicationType(communicationTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<CommunicationType> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationTypeId:communicationTypeId.toString(),
    };
    const extractor = (response:any) => new CommunicationType(response.communicationType);;
    return this.makeJsonRequest("communicationType/v1/getCommunicationType", req, false, extractor, opts);
  };

  public async getCommunicationTypes(communicationTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(CommunicationType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationTypeIds:communicationTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.communicationTypes.map((v: any) => new CommunicationType(v));;
    return this.makeJsonRequest("communicationType/v1/getCommunicationTypes", req, false, extractor, opts);
  };

  public async saveCommunicationType(communicationType: CommunicationType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationType:communicationType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("communicationType/v1/saveCommunicationType", req, true, extractor, opts);
  };

  public async setCommunicationTypeIsHidden(communicationTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationTypeId:communicationTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("communicationType/v1/setCommunicationTypeIsHidden", req, true, extractor, opts);
  };

  public async updateCommunicationTypesSequence(communicationTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    communicationTypeIds:communicationTypeIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("communicationType/v1/updateCommunicationTypesSequence", req, true, extractor, opts);
  };
}

export const communicationTypeService = new CommunicationTypeService();